// ============================================
// Northworld — Login Page (Westworld-inspired)
// ============================================
import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { authClient } from '@/shared/lib/auth-client'
import { Spinner } from '@/shared/components/Spinner'

function getInboxLink(email: string) {
  const domain = email.split('@')[1]?.toLowerCase() ?? ''
  const links: Record<string, { label: string; url: string }> = {
    'gmail.com':      { label: 'Open Gmail',       url: 'https://mail.google.com' },
    'googlemail.com': { label: 'Open Gmail',       url: 'https://mail.google.com' },
    'yahoo.com':      { label: 'Open Yahoo Mail',  url: 'https://mail.yahoo.com' },
    'yahoo.co.jp':    { label: 'Open Yahoo Mail',  url: 'https://mail.yahoo.co.jp' },
    'outlook.com':    { label: 'Open Outlook',     url: 'https://outlook.live.com' },
    'hotmail.com':    { label: 'Open Outlook',     url: 'https://outlook.live.com' },
    'live.com':       { label: 'Open Outlook',     url: 'https://outlook.live.com' },
    'icloud.com':     { label: 'Open iCloud Mail', url: 'https://www.icloud.com/mail' },
    'me.com':         { label: 'Open iCloud Mail', url: 'https://www.icloud.com/mail' },
    'proton.me':      { label: 'Open Proton Mail', url: 'https://mail.proton.me' },
    'protonmail.com': { label: 'Open Proton Mail', url: 'https://mail.proton.me' },
  }
  return links[domain] ?? null
}

// fix #3: オープンリダイレクト対策 — URL constructor で origin チェック
// getCallbackURL() と useEffect の navigate 両方で共通利用
function safeRedirect(redirect: string | null): string {
  if (!redirect) return '/'
  try {
    const url = new URL(redirect, 'https://app.northworld.ai')
    if (url.origin !== 'https://app.northworld.ai') return '/'
    return url.pathname + url.search
  } catch {
    return '/'
  }
}

export function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [email, setEmail]       = useState('')
  const [checking, setChecking] = useState(true)
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState('')
  const [sent, setSent]         = useState(false)

  useEffect(() => {
    authClient.getSession().then(({ data }: { data: { session?: unknown } | null }) => {
      if (data?.session) {
        const redirect = searchParams.get('redirect')
        // fix #3: safeRedirect 関数で統一（オープンリダイレクト対策）
        const safe = safeRedirect(redirect)
        navigate(safe, { replace: true })
      } else {
        setChecking(false)
      }
    }).catch(() => {
      // Network error / auth unreachable — show login form anyway
      setChecking(false)
    })
  }, [navigate, searchParams])

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#081018]">
        <Spinner size="md" />
      </div>
    )
  }

  const getCallbackURL = () => {
    const redirect = searchParams.get('redirect')
    // fix #3: safeRedirect で origin チェック後に絶対URLを構築
    const safe = safeRedirect(redirect)
    return `https://app.northworld.ai${safe}`
  }

  const handleGoogleSignIn = () => {
    setLoading(true)
    setError('')
    const callbackURL = encodeURIComponent(getCallbackURL())
    const authUrl = `https://app.northworld.ai/login/google?callbackURL=${callbackURL}`

    // PWA (standalone) or iOS WebView では window.open でSafari外部起動
    // Google OAuthはWebViewをブロックするため、システムブラウザ経由が必須
    const isPWA = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true

    if (isPWA) {
      // 外部Safariで開く
      window.open(authUrl, '_blank', 'noopener')

      // Safari側でOAuth完了後、PWAに戻ってきたタイミングでセッションをポーリング確認
      // visibilitychange: ユーザーがPWAに戻ってきた時に発火
      let checked = false
      const onVisible = () => {
        if (checked) return
        checked = true
        document.removeEventListener('visibilitychange', onVisible)

        authClient.getSession().then(({ data }: { data: { session?: unknown } | null }) => {
          if (data?.session) {
            const redirect = searchParams.get('redirect')
            const safe = safeRedirect(redirect)
            navigate(safe || '/', { replace: true })
          } else {
            // まだ未完了の場合はローディングを解除してユーザーに再試行させる
            setLoading(false)
            setError('Sign-in not completed. Please try again.')
          }
        }).catch(() => {
          setLoading(false)
          setError('Could not verify sign-in. Please try again.')
        })
      }
      document.addEventListener('visibilitychange', onVisible)
    } else {
      // 通常ブラウザ: そのままリダイレクト
      window.location.href = authUrl
    }
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    setLoading(true)
    setError('')

    try {
      const { error: authError } = await authClient.signIn.magicLink({
        email,
        callbackURL: getCallbackURL(),
      })
      if (authError) {
        setError((authError as { message?: string }).message || `Error. Please try again.`)
        setLoading(false)
        return
      }
      setSent(true)
      setLoading(false)
    } catch {
      setError('Network error. Please check your connection and try again.')
      setLoading(false)
    }
  }

  const inboxLink = sent ? getInboxLink(email) : null

  return (
    <div className="nw-page min-h-screen flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Grid pattern */}
      <div className="nw-grid" />

      <div className="relative z-10 flex flex-col items-center w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="mb-12 no-underline">
          <span className="nw-logo">NORTHWORLD</span>
        </Link>

        {/* Card */}
        <div className="nw-card w-full p-8 sm:p-12">
          {!sent ? (
            <>
              <div className="text-center mb-10">
                <div className="nw-divider w-10 mx-auto mb-6" />
                {/* font-display inherited from nw-page; letter-spacing via Tailwind */}
                <h1 className="text-[20px] tracking-[0.2em] font-light text-white/90 mb-3">
                  ENTER
                </h1>
                <p className="text-[12px] font-light tracking-[0.05em] text-[color:color-mix(in oklch, var(--color-nw), transparent 60%)]">
                  Connect with your AI companions
                </p>
              </div>

              {/* Google OAuth */}
              <button
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="w-full py-3.5 px-4 flex items-center justify-center gap-3 font-light text-[12px] uppercase tracking-[0.15em]
                  transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg
                  bg-white/95 text-[#081018] border border-white/30
                  hover:shadow-[0_0_30px_rgba(255,255,255,0.15)]
                  hover:bg-white hover:border-white/50"
              >
                {loading ? (
                  <Spinner size="sm" dark />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                {loading ? 'Connecting…' : 'Continue with Google'}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-4 my-8">
                <div className="flex-1 h-px bg-[color:color-mix(in oklch, var(--color-nw), transparent 92%)]" />
                <span className="nw-label">or</span>
                <div className="flex-1 h-px bg-[color:color-mix(in oklch, var(--color-nw), transparent 92%)]" />
              </div>

              {/* Magic link form */}
              <form onSubmit={handleEmailSubmit} className="space-y-5">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="nw-input"
                  />
                  <p className="text-[10px] font-light mt-2 pl-1 tracking-[0.05em] text-[color:color-mix(in oklch, var(--color-nw), transparent 75%)]">
                    We'll email you a sign-in link
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="nw-btn-primary rounded-lg w-full py-3.5 justify-center
                    disabled:opacity-50 disabled:cursor-not-allowed
                    hover:shadow-[0_0_30px_color-mix(in oklch, var(--color-nw), transparent 75%)]"
                >
                  {loading && <Spinner size="sm" dark />}
                  {loading ? 'Sending…' : 'Send link'}
                </button>
                {error && (
                  <p className="text-[12px] font-light tracking-[0.03em] text-[#e87070]">{error}</p>
                )}
              </form>
            </>
          ) : (
            <>
              {/* Sent state */}
              <div className="text-center">
                <div className="nw-divider w-10 mx-auto mb-6" />
                <h2 className="text-[18px] tracking-[0.2em] font-light text-white/90 mb-4">
                  CHECK INBOX
                </h2>
                <p className="text-[12px] font-light mb-1 text-[color:color-mix(in oklch, var(--color-nw), transparent 60%)]">
                  Sign-in link sent to
                </p>
                <p className="text-[13px] font-light text-white/80 mb-8 tracking-[0.03em]">{email}</p>

                {inboxLink && (
                  <a
                    href={inboxLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="nw-btn-ghost flex w-full py-3 px-4 justify-center gap-2 text-[11px] mb-8"
                  >
                    {inboxLink.label} →
                  </a>
                )}

                <p className="text-[11px] font-light text-[color:color-mix(in oklch, var(--color-nw), transparent 70%)]">
                  Didn't get it?{' '}
                  <button
                    onClick={() => { setSent(false); setEmail(''); setError('') }}
                    className="underline transition-colors text-[color:color-mix(in oklch, var(--color-nw), transparent 50%)]"
                  >
                    Try again
                  </button>
                </p>
              </div>
            </>
          )}
        </div>

        {/* Back link */}
        <Link
          to="/"
          className="mt-10 text-[10px] uppercase tracking-[0.2em] font-light no-underline transition-all duration-300 text-[color:color-mix(in oklch, var(--color-nw), transparent 70%)]"
        >
          ← Back
        </Link>
      </div>
    </div>
  )
}
